// Presentation-only calendar layout helpers.
//
// IMPORTANT: This module performs ZERO scheduling logic. It does not
// decide whether bookings are legal, whether they conflict, or whether a
// stylist has capacity. The backend (scheduling.py) is the ONLY source of
// truth for booking validity. This file only answers visual questions:
//
//   1. assignLanes(bookings) — given a set of bookings whose wall-clock
//      windows may overlap (a legitimate Phase-1 state when one booking
//      slots into another's processing window), assign each one to a
//      visual lane so the DOM can render them side-by-side instead of
//      stacking on top of each other.
//
//   2. stageSegments(booking) — derive the per-stage [topMin, heightMin,
//      type] tuples a booking should render as. Stages come from the
//      backend's immutable `booking.stages[]` snapshot — we just project
//      them onto pixels.

import { isoOrder } from "./time";

/**
 * Assign each booking to a visual lane within its overlap cluster.
 *
 * An "overlap cluster" is a maximal set of bookings whose full
 * [start_at, end_at) windows pairwise touch each other transitively.
 * Within a cluster, we greedily assign each booking the lowest lane
 * index that has no still-active prior booking, producing a stable
 * column layout. Bookings in DIFFERENT clusters get their own
 * independent lane counts.
 *
 * Returns: Map<bookingId, { lane: number, total: number }>
 *   - lane:  0-based column index within the cluster
 *   - total: total number of lanes the cluster needs
 *
 * Pure function. Input is not mutated.
 */
export function assignLanes(bookings) {
  const out = new Map();
  if (!bookings || bookings.length === 0) return out;

  const sorted = [...bookings].sort(
    (a, b) => isoOrder(a.start_at) - isoOrder(b.start_at) ||
              isoOrder(a.end_at) - isoOrder(b.end_at)
  );

  let cluster = [];          // bookings currently in the open cluster
  let clusterEnd = -Infinity; // max end_at seen so far in the cluster
  const clusters = [];        // [{ items: booking[], lanes: Map<id,lane> }]

  const flush = () => {
    if (cluster.length === 0) return;
    // Greedy lane assignment within the cluster.
    const laneEnds = [];      // laneEnds[i] = end_at of last booking on lane i
    const lanes = new Map();
    for (const b of cluster) {
      const start = isoOrder(b.start_at);
      const end = isoOrder(b.end_at);
      let chosen = -1;
      for (let i = 0; i < laneEnds.length; i++) {
        if (laneEnds[i] <= start) { chosen = i; break; }
      }
      if (chosen === -1) { chosen = laneEnds.length; laneEnds.push(end); }
      else { laneEnds[chosen] = end; }
      lanes.set(b.id, chosen);
    }
    clusters.push({ items: cluster, lanes, total: laneEnds.length });
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const b of sorted) {
    const start = isoOrder(b.start_at);
    if (start >= clusterEnd) flush();
    cluster.push(b);
    clusterEnd = Math.max(clusterEnd, isoOrder(b.end_at));
  }
  flush();

  for (const c of clusters) {
    for (const [id, lane] of c.lanes) {
      out.set(id, { lane, total: c.total });
    }
  }
  return out;
}

/**
 * Derive per-stage render segments from a booking.
 *
 * Returns: Array<{ offsetMin, durationMin, type, name, key }>
 *   - offsetMin: minutes from booking.start_at
 *   - durationMin: stage length in minutes
 *   - type: "occupied" | "processing"
 *   - name: stage name (from immutable snapshot)
 *   - key: stable key for React
 *
 * If the booking has no stages (legacy row), synthesises a single
 * occupied stage covering the whole window so the renderer still works.
 *
 * Pure function. Does not call the API or mutate the booking.
 */
export function stageSegments(booking) {
  if (!booking) return [];
  const stages = Array.isArray(booking.stages) ? booking.stages : [];
  if (stages.length > 0) {
    return stages.map((st, i) => ({
      offsetMin: st.offset_minutes || 0,
      durationMin: st.duration_minutes || 0,
      type: st.type === "processing" ? "processing" : "occupied",
      name: st.name || (st.type === "processing" ? "Processing" : "Service"),
      key: st.stage_template_id || `${booking.id}-${i}`,
    }));
  }
  // Legacy fallback — render the whole window as a single occupied stage.
  return [{
    offsetMin: 0,
    durationMin: booking.duration_minutes || 30,
    type: "occupied",
    name: booking.service?.name || booking.service_name_snapshot || "Service",
    key: `${booking.id}-legacy`,
  }];
}

/**
 * True iff the booking has more than one stage, regardless of types.
 * Used by the renderer to decide whether to draw stage dividers.
 */
export function isMultiStage(booking) {
  return Array.isArray(booking?.stages) && booking.stages.length > 1;
}

/**
 * Total occupied minutes — sum of every occupied stage. Returns 0 if no
 * stages array. Used by the booking detail modal for capacity
 * messaging ("stylist is occupied for 60m of 120m").
 */
export function occupiedMinutes(booking) {
  const stages = Array.isArray(booking?.stages) ? booking.stages : [];
  if (stages.length === 0) return booking?.duration_minutes || 0;
  return stages
    .filter((s) => s.type === "occupied")
    .reduce((acc, s) => acc + (s.duration_minutes || 0), 0);
}
